import "./SelectSearchInput.css";

import { ChevronDownIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import { debounce } from "lodash";
import { ReactNode, useState } from "react";
import AsyncSelect from "react-select/async";

const DropdownIndicator = () => (
  <div style={{ color: "#ddd", height: 24, width: 32 }}>
    <svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  </div>
);

interface SelectSearchInputProps {
  id?: string | number;
  label?: string;
  field?: string;
  filter: string;
  mapToOptions: Function;
  apiService: Function;
  serviceOptions?: {};
  onBlur?: Function;
  onChange: any;
  component: any;
  children?: ReactNode;
  action?: Function;
  isMissingInfo?: boolean;
  disabled?: boolean;
  debounceTime?: number;
  inputLimit?: number;
}
const SelectSearchInput = ({
  children,
  id,
  label,
  mapToOptions,
  filter,
  apiService,
  component,
  serviceOptions = {},
  onBlur,
  onChange,
  isMissingInfo,
  disabled = false,
  debounceTime,
  inputLimit = -1,
}: SelectSearchInputProps) => {
  const [open, setOpen] = useState<boolean>(false);

  // Async Await was preventing the debounce from working properly
  const autocomplete = (input: string, callback: Function) => {
    if (input.length < inputLimit) {
      callback([]);
      return;
    }
    apiService({
      [filter]: input.length ? input : undefined,
      ...serviceOptions,
    }).then((resp: any) => {
      if (resp.results) {
        callback(mapToOptions(resp.results));
      }
    });
  };

  const debouncedAutoComplete = debounceTime
    ? debounce(autocomplete, debounceTime)
    : autocomplete;

  return (
    <div className="SelectSearchInput">
      <button
        className={classNames("SelectSearchInput__button", {
          SelectSearchInput__missingInfo: isMissingInfo,
        })}
        id={(id && `${id}`) || label}
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        type="button"
        disabled={disabled}
      >
        {children}
        <span className="SelectSearchInput__dropdownIndicator">
          <ChevronDownIcon />
        </span>
      </button>
      {open && (
        <div className="SelectSearchInput__select">
          <AsyncSelect
            isDisabled={disabled}
            // Open Automatically
            autoFocus
            // XHR Options (sends a dispatch)
            defaultOptions
            // @ts-ignore
            loadOptions={debouncedAutoComplete}
            // Control itself won't render the value
            controlShouldRenderValue={false}
            backspaceRemovesValue={false}
            isClearable={false}
            // Other Options
            hideSelectedOptions={false}
            // Force the menu open
            menuIsOpen
            onChange={(v) => {
              setOpen(false);
              onChange(v);
            }}
            onBlur={() => {
              onBlur && onBlur();
              setOpen(false);
            }}
            placeholder="Search..."
            // Remove some superflous chrome
            components={{
              DropdownIndicator,
              IndicatorSeparator: null,
            }}
            // Make additional stuff show up

            formatOptionLabel={component}
            tabSelectsValue={false}
            isMulti={false}
            //value={value}
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < inputLimit
                ? `Requires search term greater than ${
                    inputLimit - 1
                  } characters`
                : "No Options"
            }
            theme={(theme) => ({
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                primary25: "var(--purple25)",
                primary50: "var(--purple50)",
                primary75: "var(--purple75)",
                primary: "var(--purple)",
              },
            })}
            classNamePrefix="SelectSearchInput__select"
          />
        </div>
      )}
      {open && (
        <div
          className="SelectSearchInput__blanket"
          onClick={() => setOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default SelectSearchInput;
